import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';
import type { WebsiteAnalyticsFunnel } from '../types';
import { formatConversionRate } from '../utils/funnel';

interface FunnelStep {
  key: keyof WebsiteAnalyticsFunnel;
  labelKey: string;
}

const FUNNEL_STEPS: FunnelStep[] = [
  { key: 'page_view', labelKey: 'websiteActivity.funnel.pageViews' },
  { key: 'register_success', labelKey: 'websiteActivity.funnel.registrations' },
  { key: 'free_trial_success', labelKey: 'websiteActivity.funnel.freeTrials' },
  { key: 'payment_success', labelKey: 'websiteActivity.funnel.payments' },
];

interface WebsiteActivityFunnelProps {
  funnel: WebsiteAnalyticsFunnel;
}

export function WebsiteActivityFunnel({ funnel }: WebsiteActivityFunnelProps) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('websiteActivity.funnel.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-0">
        {FUNNEL_STEPS.map((step, index) => {
          const count = funnel[step.key];
          const prev = index > 0 ? funnel[FUNNEL_STEPS[index - 1]!.key] : null;
          const conversion = prev !== null ? formatConversionRate(prev, count) : null;

          return (
            <div key={step.key}>
              {index > 0 ? (
                <div className="flex flex-col items-center py-2 text-muted-foreground">
                  <Icons.ChevronRight className="h-4 w-4 rotate-90" aria-hidden />
                  {conversion ? (
                    <span className="text-xs tabular-nums">
                      {t('websiteActivity.funnel.conversion', { rate: conversion })}
                    </span>
                  ) : null}
                </div>
              ) : null}
              <div
                className={cn(
                  'flex items-center justify-between gap-4 rounded-lg border border-border px-4 py-3',
                  index === 0 && 'bg-muted/30',
                )}
              >
                <span className="text-sm font-medium text-foreground">{t(step.labelKey)}</span>
                <span className="text-xl font-bold tabular-nums text-foreground">{count}</span>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
